/**
 * HardAmenityInput.js
 *
 * Renders the correct input for a HARD (or SOFT numerical/categorical) amenity
 * based on hcm_mapping_type:
 *
 *   boolean    → toggle. ON = required=true, value=true. OFF = required=false.
 *   numerical  → multi-select chips from hcm_range_definition JSON.
 *   categorical→ multi-select chips from categorical_options.
 *
 * "مهم نیست" chip is always shown first.
 *   - Selecting it (or selecting nothing) stores ALL bucket keys as value → required=false.
 *   - Selecting specific chips stores those keys → required=true.
 *
 * Props:
 *   amenity  — API object with hcm_mapping_type, hcm_range_definition, categorical_options
 *   value    — current value: true | [1,2,3] | null
 *   onChange(newValue, required)
 */

/**
 * Parse JSON range_definition:
 *   { "1": { "rule": "< 40", "label_fa": "کمتر از ۴۰" }, ... }
 * Label priority: label_fa → rule → key.
 * No auto-translation.
 */
export function parseRangeDefinition(rangeDefinition) {
    if (!rangeDefinition || typeof rangeDefinition !== 'object') return [];
    return Object.entries(rangeDefinition)
        .map(([k, v]) => {
            const key = parseInt(k, 10);
            const rule = (v && v.rule) ? v.rule.trim() : '';
            const labelFa = (v && v.label_fa) ? v.label_fa.trim() : '';
            return { key, label: labelFa || rule || String(key) };
        })
        .filter(opt => !isNaN(opt.key) && opt.label !== '---')
        .sort((a, b) => a.key - b.key);
}

/** All numeric keys from range_definition, including hidden "---" ones. */
export function allRangeKeys(rangeDefinition) {
    if (!rangeDefinition || typeof rangeDefinition !== 'object') return [];
    return Object.keys(rangeDefinition)
        .map(Number)
        .filter(k => !isNaN(k))
        .sort((a, b) => a - b);
}

/* ── Boolean toggle ── */
function BooleanInput({ value, onChange }) {
    const on = value === true;
    return (
        <button
            type="button"
            className={`hai-bool-toggle ${on ? 'hai-bool-on' : ''}`}
            onClick={() => onChange(on ? null : true, !on)}
            aria-pressed={on}
        >
            <span className="hai-bool-knob" />
            <span className="hai-bool-label">{on ? 'الزامی ✓' : 'مهم نیست'}</span>
        </button>
    );
}

/* ── Multi-select chips with "مهم نیست" first ── */
// allKeys = full key set (including hidden "---" ones) used for "مهم نیست" value
// options = visible chips only
function ChipSelect({ options, allKeys, value, onChange }) {
    // "مهم نیست" = value is null, empty, or contains all keys (full set)
    const isAllSelected = !value || !Array.isArray(value) || value.length === 0
        || (value.length === allKeys.length);

    const selSet = new Set((Array.isArray(value) ? value : []).map(String));

    const toggleAll = () => {
        // "مهم نیست" → send full key set (including hidden), required=false
        onChange(allKeys, false);
    };

    const toggleKey = (key) => {
        const k = String(key);
        let next;
        if (isAllSelected) {
            // "مهم نیست" state → start a fresh selection with just this key
            next = [key];
        } else if (selSet.has(k)) {
            next = [...selSet].filter(x => x !== k).map(Number);
        } else {
            next = [...selSet, k].map(Number);
        }
        if (next.length === 0 || next.length === allKeys.length) {
            onChange(allKeys, false);
        } else {
            onChange(next, true);
        }
    };

    return (
        <div className="hai-chips">
            {/* "مهم نیست" always first */}
            <button
                type="button"
                className={`hai-chip hai-chip-any ${isAllSelected ? 'hai-chip-on' : ''}`}
                onClick={toggleAll}
            >
                {isAllSelected && <span className="hai-chip-check">✓</span>}
                مهم نیست
            </button>

            {options.map(opt => {
                const active = !isAllSelected && selSet.has(String(opt.key));
                return (
                    <button
                        key={opt.key}
                        type="button"
                        className={`hai-chip ${active ? 'hai-chip-on' : ''}`}
                        onClick={() => toggleKey(opt.key)}
                    >
                        {active && <span className="hai-chip-check">✓</span>}
                        {opt.label}
                    </button>
                );
            })}
        </div>
    );
}

export default function HardAmenityInput({ amenity, value, onChange }) {
    const mappingType = amenity.hcm_mapping_type || amenity.value_type;

    if (mappingType === 'boolean') {
        return <BooleanInput value={value} onChange={onChange} />;
    }

    if (mappingType === 'numerical') {
        const options = parseRangeDefinition(amenity.hcm_range_definition);
        const fullKeys = allRangeKeys(amenity.hcm_range_definition);
        if (!options.length) return null;
        return <ChipSelect options={options} allKeys={fullKeys} value={value} onChange={onChange} />;
    }

    if (mappingType === 'categorical') {
        const opts = amenity.categorical_options || {};
        const options = Object.entries(opts)
            .map(([k, label]) => ({ key: isNaN(Number(k)) ? k : Number(k), label }));
        if (!options.length) return null;
        // categorical has no hidden keys — allKeys == visible keys
        return <ChipSelect options={options} allKeys={options.map(o => o.key)} value={value} onChange={onChange} />;
    }

    return null;
}
